import { useEffect, useMemo } from 'react'
import { CaptionSM } from '@/atoms/Text'
import { useLegacyEffect } from '@/molecules/utils'
import { useSafeAnalytics } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import { CheckInputButton } from '../../components/CheckInputButton'
import { Page } from '../../components/Page'
import { PageHeading } from '../../components/PageHeading'
import { TorchApplicationStatus, useTorchApplication } from '../../components/TorchApplicationContext'
import { useDelayedSelection } from '../../components/useDelayedSelection'
import { filterDraftTorchApplications } from './torchApplicationHelpers'
import { ExistingUserStep } from './types'

enum SubmissionType {
  New = 'new_submission',
  Resubmission = 'resubmission',
  Continue = 'continue',
  Review = 'review',
}

type SubmissionButtonData = {
  name: SubmissionType
  label: string
  caption?: string
}

export const ExistingUserIndex = () => {
  const { t } = useTranslate('guild-create')
  const { setPageStep, torchApplications, refetchApplications } = useTorchApplication()
  const { track } = useSafeAnalytics()

  useLegacyEffect(() => {
    track('Guild Create Page Viewed')
  }, [track])

  useEffect(() => {
    refetchApplications()
  }, [refetchApplications])

  const [selection, onClick] = useDelayedSelection((nextSelected) => {
    if (nextSelected === SubmissionType.Continue || nextSelected === SubmissionType.Review) {
      setPageStep(ExistingUserStep.Continue)
    } else if (nextSelected === SubmissionType.New) {
      setPageStep(ExistingUserStep.Type)
    } else if (nextSelected === SubmissionType.Resubmission) {
      setPageStep(ExistingUserStep.Resubmission)
    }
  })

  const draftApplications = filterDraftTorchApplications(torchApplications)
  const approvedApplications = torchApplications.filter((appl) => appl?.status === TorchApplicationStatus.Approved)

  const buttonsData: SubmissionButtonData[] = useMemo(() => {
    const draftApplicationCount = draftApplications.length

    const buttonsData: SubmissionButtonData[] = [
      {
        name: SubmissionType.New,
        label: t('submitNewProject', 'Submit New Project'),
      },
    ]

    if (approvedApplications.length) {
      buttonsData.unshift({
        name: SubmissionType.Resubmission,
        label: t('resubmitProject', 'Resubmit Project'),
      })
    }

    if (draftApplicationCount) {
      buttonsData.unshift({
        name: SubmissionType.Continue,
        label: t('continueEditing', 'Continue Editing'),
        caption: t('numInProgress', '{{ n }} in progress', { n: draftApplicationCount }),
      })
    }

    return buttonsData
  }, [draftApplications, t, approvedApplications])

  return (
    <Page>
      <PageHeading>{t('whatTypeOfSubmissionIsThis', 'What type of submission is this?')}</PageHeading>
      <div className="flex flex-col items-stretch gap-4 lg:flex-row">
        {buttonsData.map(({ name, caption, label }) => (
          <CheckInputButton key={name} name={name} selected={selection === name} onClick={onClick}>
            <div className="flex flex-col">
              {label}
              {caption && <CaptionSM color="core-gray-500">{caption}</CaptionSM>}
            </div>
          </CheckInputButton>
        ))}
      </div>
    </Page>
  )
}
